/* eslint-disable */
// =============================================================================
// Diploma landing — SHARED CONTRACT (SPEC.md §B.1). DO NOT EDIT LOCALLY.
// Identical copies (same basename; landing.logic.ts sits next to each):
//   BE    src/modules/v1/diplomas/landing/landing.contract.ts
//   ADM   src/@types/landing/landing.contract.ts
//   FE    src/types/landing/landing.contract.ts
// Conventions: bilingual pairs are `x` / `x_ar`; empty text is '' (never null);
// unknown numbers are null; dates are ISO-8601 strings; months are 'YYYY-MM'.
// An enum whose list has no '' (kind, status, sourceType, quoteOriginal,
// provider) is REQUIRED: '' is not a value for it, and the BE never defaults it.
// Array order IS display order (there is no `order` field on landing items).
// =============================================================================

export const LANDING_CONTRACT_VERSION = '1.2.0';

// ---- enums -------------------------------------------------------------------
export const LEVELS = ['beginner', 'intermediate', 'advanced'] as const;
export type Level = typeof LEVELS[number];

/** live = fully live on Zoom, no recordings promised;
 *  live_recorded = live on Zoom + every session recorded;
 *  blended = recorded lessons + weekly live sessions. */
export const DELIVERY_FORMATS = ['live', 'live_recorded', 'blended'] as const;
export type DeliveryFormat = typeof DELIVERY_FORMATS[number];

/** '' (not set) is the default: nothing about language is printed until an editor sets it. */
export const INSTRUCTION_LANGUAGES = ['ar_en_terms', 'ar', 'en'] as const;
export type InstructionLanguage = typeof INSTRUCTION_LANGUAGES[number];

export const LANDING_STATUSES = ['draft', 'published'] as const;
export type LandingStatus = typeof LANDING_STATUSES[number];

export const MODULE_KINDS = ['module', 'project', 'course'] as const;
export type ModuleKind = typeof MODULE_KINDS[number];

export const INSTALLMENT_PROVIDERS = ['valu', 'aman', 'souhoola'] as const;
export type InstallmentProvider = typeof INSTALLMENT_PROVIDERS[number];
/** Providers NAMED on the page when the server env LANDING_INSTALLMENT_PROVIDERS is unset
 *  (owner decision O9: Aman is named only after a confirmed test payment). */
export const DEFAULT_NAMED_PROVIDERS: InstallmentProvider[] = ['valu', 'souhoola'];

export const STORY_STATUSES = ['graduated', 'in_progress'] as const;
export type StoryStatus = typeof STORY_STATUSES[number];

export const STORY_SOURCES = ['interview', 'form', 'google_review', 'linkedin_post', 'whatsapp', 'in_platform'] as const;
export type StorySource = typeof STORY_SOURCES[number];

export const AFTER_EVIDENCE = ['', 'linkedin', 'employer_letter', 'self_reported'] as const;
export type AfterEvidence = typeof AFTER_EVIDENCE[number];

export const CONSENT_METHODS = ['', 'form', 'whatsapp', 'email', 'signed'] as const;
export type ConsentMethod = typeof CONSENT_METHODS[number];

export const PROJECT_LINK_KINDS = ['', 'live', 'github', 'powerbi', 'kaggle', 'video', 'report'] as const;
export type ProjectLinkKind = typeof PROJECT_LINK_KINDS[number];

/** Site-wide FAQ defaults (copy lives in FE i18n `diplomaLanding.siteFaq.<key>`). */
export const SITE_FAQ_KEYS = [
  'installments', 'payment_methods', 'extra_fees', 'refund', 'defer', 'missed_session',
  'access', 'late_join', 'timezone', 'certificate', 'language', 'non_cs', 'tech', 'premium',
  'after_payment', 'account', 'parent_pays', 'company_pays', 'promo', 'payment_safe',
  'paid_not_enrolled', 'why_pay', 'outside_egypt',
] as const;
export type SiteFaqKey = typeof SITE_FAQ_KEYS[number];
/** Site-wide OFF keys when the FE server env LANDING_SITE_FAQ_DISABLED is unset (O11).
 *  The env (comma-separated keys) replaces this list without a deploy of three repos. */
export const SITE_FAQ_DEFAULT_DISABLED: SiteFaqKey[] = ['outside_egypt'];
/** Keys an editor cannot hide (the admin shows them as "Always shown"; the BE drops them from hiddenSiteFaqKeys). */
export const SITE_FAQ_ALWAYS: SiteFaqKey[] = ['refund', 'payment_methods'];

export const LANDING_TABS = [
  'overview', 'fit', 'outcomes', 'syllabus', 'projects', 'people', 'alumni',
  'certificate', 'pricing', 'faq', 'brochure', 'leads',
] as const;
export type LandingTab = typeof LANDING_TABS[number];

export const BATCH_STATES = ['hidden', 'finished', 'closed', 'full', 'late_join', 'starting_soon', 'open'] as const;
export type BatchState = typeof BATCH_STATES[number];
/** States a buyer can pay for. */
export const BOOKABLE_STATES: BatchState[] = ['open', 'starting_soon', 'late_join'];

export const LEAD_KINDS = ['waitlist', 'brochure'] as const;
export type LeadKind = typeof LEAD_KINDS[number];

/** Page sections whose visibility is data-driven (hero, proof strip and final CTA always render). */
export const SECTION_IDS = [
  'about', 'fit', 'outcomes', 'syllabus', 'projects', 'instructor', 'how',
  'alumni', 'stories', 'certificate', 'pricing', 'faq',
] as const;
export type SectionId = typeof SECTION_IDS[number];
export type SectionVisibility = Record<SectionId, { ar: boolean; en: boolean }>;

// ---- limits (validated by BE; enforced in ADM inputs) -------------------------
// Field → limit (SPEC §B.3 table):
//   shortText  every title (item, phase, module, session, project, topic group, career), names of tools
//   text       topic items, outcomes, deliverables, session theory/practice/result,
//              TextItem.text, TitledTextItem.text, roleBefore/roleAfter use `role`
//   longText   module + phase summary, project brief, StudentProject.summary, certificate statement,
//              refundSummary
//   quote      Story.quote;  faqAnswer  FaqItem.answer;  shortText  FaqItem.question
//   editorNotes landing + curriculum notes;  moduleEditorNotes  module notes
export const LANDING_LIMITS = {
  tagline: 120, shortText: 160, text: 300, longText: 600, quote: 400, name: 80,
  role: 80, url: 500, editorNotes: 4000, moduleEditorNotes: 2000, faqAnswer: 1200,
  seoTitle: 70, seoDescription: 160,
  audience: 6, notFor: 4, prerequisites: 8, techRequirements: 6, outcomes: 12, careers: 8,
  tools: 60, instructorIds: 4, moduleInstructors: 2, support: 6, included: 10, installmentPlans: 4, faqs: 30,
  stories: 24, studentProjects: 24, pastCohorts: 30, certificateCriteria: 6, projectTools: 12,
  phases: 6, modules: 40, topicGroups: 12, itemsPerGroup: 40, sessionsPerModule: 12,
  moduleOutcomes: 10, moduleProjects: 4, moduleTools: 25, deliverables: 10,
  weeklySelfStudyHours: 40, sessionMinutesMin: 30, sessionMinutesMax: 300, weekMax: 104,
  brochureBytes: 5242880,
} as const;

/** Item ids: admin generates them; the DTO accepts any string ≤ 40 (incl. '');
 *  normalizeLanding re-issues every id that fails this pattern or repeats in its list. */
export const ID_PATTERN = '^[A-Za-z0-9_-]{1,40}$';
/** Curriculum phase/module/project keys (stable deep links: #module-<key>); never ''. */
export const KEY_PATTERN = '^[a-z0-9-]{1,40}$';
/** References to a key that may be empty: module.phaseKey, StudentProject.plannedProjectKey. */
export const OPTIONAL_KEY_PATTERN = '^([a-z0-9-]{1,40})?$';
/** 'YYYY-MM' or '' (PastCohort months, Story.collectedAt). */
export const MONTH_PATTERN = '^(\\d{4}-(0[1-9]|1[0-2]))?$';

// ---- building blocks -----------------------------------------------------------
export interface LandingItemBase {
  id: string;        // ID_PATTERN, unique within its list
  visible: boolean;  // default true; false = kept in admin, never public
  isDemo: boolean;   // default false; see SPEC §B.7
}
export interface TextItem extends LandingItemBase { text: string; text_ar: string; }
export interface TitledTextItem extends LandingItemBase {
  title: string; title_ar: string;   // required to render in that language
  text: string; text_ar: string;     // optional one-liner
}
export interface ToolItem extends LandingItemBase {
  name: string;      // Latin, same in both languages (e.g. "Power BI")
  logo: string;      // optional image URL
}
export interface FaqItem extends LandingItemBase {
  question: string; question_ar: string;
  answer: string; answer_ar: string;
}
export interface InstallmentPlan extends LandingItemBase {
  provider: InstallmentProvider;  // required, no default
  months: number;               // 3..60
  monthlyAmount: number;        // EGP, > 0
  interestFree: boolean;        // only if contracted
  totalAmount: number | null;   // EGP; required when !interestFree; must be >= price
  validUntil: string | null;    // ISO date; plan hides after this date
}
export interface Consent {
  given: boolean;
  date: string | null;          // 'YYYY-MM-DD'
  method: ConsentMethod;
  note: string;                 // scope, who collected it
}
/** OFF-PLATFORM LEDGER ONLY: a past batch that is NOT in Diploma.batches (e.g. DS batches 1–4
 *  run before the platform). The BE rejects a visible row whose batchNumber equals the
 *  displayNumber of a batch it knows (400 "past cohort duplicates batch <n>"). */
export interface PastCohort extends LandingItemBase {
  batchNumber: number | null;
  label: string; label_ar: string;   // optional, e.g. "Batch 3"; default rendering uses batchNumber
  startMonth: string;                // MONTH_PATTERN
  endMonth: string;                  // MONTH_PATTERN; a row renders only with a label/number AND endMonth
  students: number | null;           // shown only when > 0
  graduates: number | null;          // shown only when > 0
  source: string;                    // REQUIRED (admin-only), e.g. "Ops roster DS-B3"
}
/** A consented story renders as a story card when its quote side for the page language is set,
 *  and as a GRADUATE CARD (photo/monogram, name, batch, status, role, LinkedIn) when it is not. */
export interface Story extends LandingItemBase {
  name: string; name_ar: string;               // as approved by the student
  photo: string;                               // real photo URL or ''
  batchNumber: number | null;
  status: StoryStatus;                         // required, no default (never assume 'graduated')
  roleBefore: string; roleBefore_ar: string;   // ≤ role
  roleAfter: string; roleAfter_ar: string;     // optional
  company: string;                             // shown only when afterEvidence === 'linkedin'
  afterEvidence: AfterEvidence;
  quote: string; quote_ar: string;             // optional; original words on one side, translation on the other
  quoteOriginal: 'ar' | 'en';                  // required; schema default 'ar'
  linkedInUrl: string;
  sourceType: StorySource;                     // required, no default
  sourceUrl: string;                           // e.g. the LinkedIn post or Google review
  collectedAt: string;                         // MONTH_PATTERN
  userId: string;                              // '' or a User id → server computes `verified`
  consent: Consent;                            // required (given=true) to render
}
export interface StudentProject extends LandingItemBase {
  title: string; title_ar: string;
  studentName: string; studentName_ar: string;
  batchNumber: number | null;
  status: StoryStatus;               // required, no default
  plannedProjectKey: string;         // OPTIONAL_KEY_PATTERN: '' or a curriculum project key (e.g. 'p10-2')
  summary: string; summary_ar: string;   // problem + the student's own result, ≤ text
  tools: string[];
  coverImage: string;                // required to render (16:10 screenshot)
  url: string;
  urlKind: ProjectLinkKind;
  consent: Consent;                  // required (given=true) to render
}
export interface LandingCertificate {
  statement: string; statement_ar: string;   // optional, BOTH or NEITHER; default = site copy "Kayfa certificate of completion"
  criteria: TextItem[];                      // how to earn it
  sampleImage: string;                       // anonymised sample
  sampleAnonymised: boolean;                 // must be true for sampleImage to render or count
}
export interface BrochureFile {
  url: string;                 // uploaded via /files/upload-course-document (download=true)
  fileName: string;            // original file name (admin display only)
  bytes: number;               // from the uploaded File (≤ brochureBytes)
  pages: number | null;        // counted by the admin before upload; null if unknown
  uploadedAt: string;          // ISO
  isDemo: boolean;             // "demo copy, not approved for production"
}
/** Public projection: the admin-only file name is stripped. */
export type PublicBrochureFile = Omit<BrochureFile, 'fileName'>;
export interface LandingSeo {
  title: string; title_ar: string;               // ≤ seoTitle; '' = pattern
  description: string; description_ar: string;   // ≤ seoDescription; '' = pattern
  ogImage: string;                               // '' = diploma image
}
export interface LandingEditor { _id: string; name: string; }

// ---- curriculum (syllabus-structure.md §2, amended: no `order`, `visible` added) --
export interface BilingualText { text: string; text_ar: string; }
export interface TopicGroup { title: string; title_ar: string; items: BilingualText[]; }
export interface CurriculumSession {
  number: number | null;         // global session number, unique in the curriculum
  title: string; title_ar: string;
  theory: string; theory_ar: string;
  practice: string; practice_ar: string;
  result: string; result_ar: string;
  minutes: number | null;        // overrides curriculum.sessionMinutes
}
export interface CurriculumProject {
  key: string;                   // KEY_PATTERN, unique in the curriculum, e.g. 'p10-1'
  title: string; title_ar: string;
  brief: string; brief_ar: string;           // ≤ longText
  deliverables: BilingualText[];
  isCapstone: boolean;
  weekStart: number | null; weekEnd: number | null;   // both or neither
  image: string;
}
export interface CurriculumPhase {
  key: string;                   // KEY_PATTERN, unique
  title: string; title_ar: string;
  summary: string; summary_ar: string;
  timing: string; timing_ar: string;         // shown only when its modules have no weeks
}
export interface CurriculumModule {
  key: string;                   // KEY_PATTERN, unique, immutable once published
  phaseKey: string;              // OPTIONAL_KEY_PATTERN: '' when there are no phases; else an existing phase key
  kind: ModuleKind;              // required; schema default 'module'
  title: string; title_ar: string;
  summary: string; summary_ar: string;
  weekStart: number | null; weekEnd: number | null;   // both or neither; 1-based, inclusive
  sessionCount: number | null;   // only when sessions[] is empty
  liveHours: number | null;      // explicit override (step 0.5)
  recordedHours: number | null;
  tools: string[];
  topicGroups: TopicGroup[];
  sessions: CurriculumSession[];
  outcomes: BilingualText[];
  projects: CurriculumProject[];
  editorNotes: string;           // admin-only
  visible: boolean;              // false = excluded from the page and from every computed total
  // "This part is taught by" (display only; FEATURES B1). Instructor ids, each one of
  // landing.instructorIds, <= LANDING_LIMITS.moduleInstructors, deduped. [] = not stated.
  // "Parts mode" is not stored: it is on when any VISIBLE module lists someone.
  instructorIds: string[];
}
export interface Curriculum {
  phases: CurriculumPhase[];
  modules: CurriculumModule[];
  sessionMinutes: number | null; // designed length of one live session
  updatedAt: string | null;      // SERVER-SET when curriculum content changes; ignored on input
  editorNotes: string;           // admin-only
}

// ---- the stored landing document ------------------------------------------------
export interface DiplomaLanding {
  status: LandingStatus;
  publishedAt: string | null;    // server-set: last draft→published transition
  updatedAt: string | null;      // server-set on every landing save
  updatedBy: LandingEditor | null;  // server-set; admin-only
  editorNotes: string;           // admin-only; seeds record draft copy and demo data here

  // hero
  tagline: string; tagline_ar: string;         // ≤ tagline
  level: Level | '';
  format: DeliveryFormat | '';
  instructionLanguage: InstructionLanguage | '';  // default '' = not set (nothing printed)
  weeklySelfStudyHours: number | null;         // optional estimate, 1..40

  // fit
  audience: TitledTextItem[];
  notFor: TextItem[];
  prerequisites: TextItem[];
  noPrerequisites: boolean;
  techRequirements: TextItem[];

  // outcomes
  outcomes: TextItem[];
  careers: TitledTextItem[];

  // syllabus
  curriculum: Curriculum;
  tools: ToolItem[];                           // empty = derived from module tools

  // people
  instructorIds: string[];                     // Instructor ObjectIds; empty = default batch instructor
  support: TitledTextItem[];                   // "who helps you between sessions"

  // students who joined before
  pastCohorts: PastCohort[];
  stories: Story[];
  studentProjects: StudentProject[];

  // certificate
  certificate: LandingCertificate;

  // pricing (display only; the charge is always core `price` in EGP)
  installmentPlans: InstallmentPlan[];
  included: TextItem[];                        // typed extras after the computed lines
  refundSummary: string; refundSummary_ar: string;   // BOTH or NEITHER; '' = site default line

  // faq
  faqs: FaqItem[];
  hiddenSiteFaqKeys: SiteFaqKey[];             // SITE_FAQ_ALWAYS keys are dropped by the BE

  // brochures
  brochure: { en: BrochureFile | null; ar: BrochureFile | null };

  // seo
  seo: LandingSeo;
}

/** What the admin sends. Server-set fields are ignored if present. */
export type DiplomaLandingInput = Omit<DiplomaLanding, 'publishedAt' | 'updatedAt' | 'updatedBy'>;

export interface LandingSaveRequest {
  landing: DiplomaLandingInput;
  expectedUpdatedAt: string | null;  // landing.updatedAt as loaded; null for a first save
}

// ---- batches (core additions + public projection) ---------------------------------
/** New fields on the Batch sub-document (edited in the core diploma modal). */
export interface BatchLandingFields {
  number: number | null;       // 1..999 → "Batch 5" / "الدفعة الخامسة"
  enrolUntil: string | null;   // ISO date; enrolment closes at 23:59:59 Cairo that day
}
export interface PublicBatchSchedule {
  weekdays: number[];          // 0 = Sunday … 6 = Saturday (BatchConfig)
  startTime: string;           // 'HH:mm'
  timezone: string;            // IANA, normally 'Africa/Cairo'
  durationMinutes: number;
  weeklyLiveMinutes: number;   // weekdays.length × durationMinutes
}
export interface PublicInstructorCard {
  _id: string;
  name: string; name_ar: string;
  imageUrl: string;
  jobTitle: string; jobTitle_ar: string;
  companyName: string; companyName_ar: string;
  about: string; about_ar: string;
  linkedInUrl: string;
  videoUrl: string;            // '' unless the host is allowed (SPEC §C.3)
  courses: { title: string; title_ar: string; slug: string }[];  // ≤ 3 public courses they teach ("Also teaches on Kayfa")
  // Parts mode: the VISIBLE modules that list this person, in syllabus order (links to
  // #module-<key>). [] when no visible module lists anyone, and on batch cards.
  teaches: { key: string; title: string; title_ar: string }[];
}
export interface PublicBatch {
  _id: string;
  name: string;                // raw stored name — never rendered on AR pages
  displayNumber: number | null;
  startDate: string | null;
  endDate: string | null;
  closesAt: string | null;     // ISO instant
  state: BatchState;           // public lists only carry open|starting_soon|late_join|full
  bookable: boolean;
  sessionsScheduled: number;   // published, non-cancelled batchSessions (0 = no class links exist yet)
  secondSessionAt: string | null;  // scheduledAt of the 2nd such session (refund rule); null if < 2
  capacity: number | null;     // null when unset or <= 0
  seatsLeft: number | null;    // non-null only when showSeatsLeft
  showSeatsLeft: boolean;
  enrolledShown: number | null;  // non-null only when >= 15
  classSize: number | null;      // capacity when 0 < capacity <= 60
  schedule: PublicBatchSchedule | null;
  instructor: PublicInstructorCard | null;     // the batch lead (kept for compatibility)
  instructors: PublicInstructorCard[];         // the batch's public team: lead first, then co-instructors; active only
  mentorsCount: number;
}

// ---- computed values (BE is the only implementation; ADM mirrors for live feedback) --
export interface CurriculumTotals {
  weeks: number | null;        // max(weekEnd) over ALL visible modules with weeks (courses included),
                               // only when every visible non-course module has weeks
  modules: number;             // visible non-course modules
  courses: number;             // visible kind='course'
  sessions: number | null;     // only when every non-course module has a session count
  liveHours: number | null;    // only when every non-course module has hours
  recordedHours: number | null;
  projects: number;            // every project in visible modules (admin info; the page uses projectsShown)
  capstones: number;
  updatedAt: string | null;
}
export interface ModuleComputed {
  key: string;
  number: number | null;       // 1..n among visible non-course modules; null for courses
  sessions: number | null;
  liveHours: number | null;
  hasBody: { en: boolean; ar: boolean };
}
/** Coverage counts every visible module that has weeks (week-placed courses included). */
export interface WeekMap { complete: boolean; overlaps: number[]; gaps: number[]; missing: string[]; /* non-course module keys without weeks */ }
export interface TrackRecord {
  platformStudents: number;    // Σ countEnrolmentsForBatch over Diploma.batches
  ledgerStudents: number;      // Σ students of visible, renderable pastCohorts
  studentsAllTime: number;     // platformStudents + ledgerStudents (ledger rows can never repeat a known batch)
  finishedCohorts: number;     // finished batches + visible pastCohorts with endMonth ≤ this month
  show: boolean;               // studentsAllTime ≥ 30 or finishedCohorts ≥ 2
  includesDemo: boolean;       // a demo pastCohort contributed → the page tags the line "Demo data"
}
export interface LandingComputed {
  mode: 'selling' | 'waitlist';
  defaultBatchId: string | null;
  effectiveWeeks: number | null;
  sessionsPerWeek: number | null;     // default batch schedule
  sessionMinutes: number | null;      // default batch schedule
  weeklyLiveMinutes: number | null;   // default batch schedule
  totals: CurriculumTotals;
  projectsShown: { ar: number; en: number };  // projects that render in #projects on that page (0 when hidden)
  modules: ModuleComputed[];
  weeks: WeekMap;
  trackRecord: TrackRecord;
  demoVisible: boolean;
}

// ---- public projection (GET /v1/diplomas/slug/:slug) --------------------------------
export type PublicCurriculumModule = Omit<CurriculumModule, 'editorNotes' | 'visible'>;
export interface PublicCurriculum {
  phases: CurriculumPhase[];
  modules: PublicCurriculumModule[];   // visible modules only
  sessionMinutes: number | null;
  updatedAt: string | null;
}
export type PublicPastCohort = Omit<PastCohort, 'source'>;
export type PublicStory = Omit<Story, 'consent' | 'userId'> & { verified: boolean };
export type PublicStudentProject = Omit<StudentProject, 'consent'>;
export interface PublicLanding {
  status: LandingStatus;
  publishedAt: string | null;
  updatedAt: string | null;
  tagline: string; tagline_ar: string;
  level: Level | '';
  format: DeliveryFormat | '';
  instructionLanguage: InstructionLanguage | '';
  weeklySelfStudyHours: number | null;
  audience: TitledTextItem[];
  notFor: TextItem[];
  prerequisites: TextItem[];
  noPrerequisites: boolean;
  techRequirements: TextItem[];
  outcomes: TextItem[];
  careers: TitledTextItem[];
  curriculum: PublicCurriculum;
  tools: ToolItem[];                        // RESOLVED list (explicit or derived)
  instructors: PublicInstructorCard[];      // RESOLVED list (explicit or fallback)
  support: TitledTextItem[];
  pastCohorts: PublicPastCohort[];
  stories: PublicStory[];
  studentProjects: PublicStudentProject[];
  certificate: LandingCertificate;          // sampleImage is '' unless sampleAnonymised
  installmentPlans: InstallmentPlan[];      // valid, visible, unexpired only
  included: TextItem[];
  refundSummary: string; refundSummary_ar: string;
  faqs: FaqItem[];
  hiddenSiteFaqKeys: SiteFaqKey[];
  brochure: { en: PublicBrochureFile | null; ar: PublicBrochureFile | null };
  seo: LandingSeo;
}
export interface PublicDiplomaLanding {
  _id: string;
  slug: string;
  title: string; title_ar: string;
  description: string; description_ar: string;
  image: string;
  price: number;                       // EGP, the charged price
  durationWeeks: number;               // stored core value (may be 0)
  syllabusPdf: string;                 // legacy, unused by the new page
  tracks: { _id: string; title: string; title_ar: string; image: string; slug: string }[];
  courses: { _id: string; name: string; title: string; image: string }[];
  landing: PublicLanding;
  batches: PublicBatch[];              // open|starting_soon|late_join|full, by startDate asc
  computed: LandingComputed;
  updatedAt: string;                   // max(diploma.updatedAt, landing.updatedAt)
}
export interface PublicBatchesResponse {   // GET /v1/diplomas/slug/:slug/batches (no-store)
  mode: 'selling' | 'waitlist';
  defaultBatchId: string | null;
  batches: PublicBatch[];
}
export interface PublicDiplomaCard {       // GET /v1/diplomas/cards
  _id: string;
  slug: string;
  title: string; title_ar: string;
  description: string; description_ar: string;
  image: string;
  price: number;
  tagline: string; tagline_ar: string;
  level: Level | '';
  effectiveWeeks: number | null;
  mode: 'selling' | 'waitlist';
  nextBatch: { _id: string; displayNumber: number | null; startDate: string | null; state: BatchState; closesAt: string | null } | null;
  updatedAt: string;
}

// ---- readiness (publish gate + page-strength meter) -----------------------------
export const REQUIRED_CHECK_KEYS = [
  'core.title', 'core.description', 'core.image', 'core.price',
  'hero.tagline', 'hero.level', 'hero.format', 'duration',
  'fit.audience', 'fit.prerequisites', 'outcomes.items', 'outcomes.careers',
  'syllabus.modules', 'syllabus.bodies', 'projects.brief',
  'instructor.complete', 'certificate.complete', 'batches.schedule', 'demo.none',
] as const;
export type RequiredCheckKey = typeof REQUIRED_CHECK_KEYS[number];

export const RECOMMENDED_CHECK_KEYS = [
  'fit.notFor', 'fit.tech', 'outcomes.six', 'outcomes.careersThree', 'syllabus.sessionLength',
  'brochure.ar', 'brochure.en', 'pricing.installmentPlan', 'alumni.stories', 'alumni.projects',
  'alumni.trackRecord', 'faq.three', 'people.support', 'hero.selfStudy', 'seo.description',
  'hero.language',
] as const;
export type RecommendedCheckKey = typeof RECOMMENDED_CHECK_KEYS[number];

export const WARNING_KEYS = [
  'weeks.overlap', 'weeks.gap', 'weeks.mismatch', 'weeks.incomplete', 'batch.startedNoWindow',
  'batch.numberUnbacked', 'plan.invalid', 'syllabus.outlineEn', 'syllabus.outlineAr',
  'projects.titleOnly', 'instructor.fallback', 'instructor.incomplete', 'instructor.oneLanguage',
  'schedule.lengthMismatch', 'schedule.countMismatch', 'demo.present', 'story.stale',
  'brochure.demo', 'published.belowGate',
  'instructor.partsPartial', 'instructor.teachesNothing', 'instructor.notOnBatch', 'instructor.moduleHidden',
] as const;
export type WarningKey = typeof WARNING_KEYS[number];

/** The admin tab that owns each check / warning (the checklist line jumps there). */
export const CHECK_TABS: Record<RequiredCheckKey | RecommendedCheckKey, LandingTab> = {
  'core.title': 'overview', 'core.description': 'overview', 'core.image': 'overview', 'core.price': 'overview',
  'hero.tagline': 'overview', 'hero.level': 'overview', 'hero.format': 'overview', 'duration': 'syllabus',
  'fit.audience': 'fit', 'fit.prerequisites': 'fit', 'outcomes.items': 'outcomes', 'outcomes.careers': 'outcomes',
  'syllabus.modules': 'syllabus', 'syllabus.bodies': 'syllabus', 'projects.brief': 'projects',
  'instructor.complete': 'people', 'certificate.complete': 'certificate', 'batches.schedule': 'overview',
  'demo.none': 'overview',
  'fit.notFor': 'fit', 'fit.tech': 'fit', 'outcomes.six': 'outcomes', 'outcomes.careersThree': 'outcomes',
  'syllabus.sessionLength': 'syllabus', 'brochure.ar': 'brochure', 'brochure.en': 'brochure',
  'pricing.installmentPlan': 'pricing', 'alumni.stories': 'alumni', 'alumni.projects': 'alumni',
  'alumni.trackRecord': 'alumni', 'faq.three': 'faq', 'people.support': 'people', 'hero.selfStudy': 'overview',
  'seo.description': 'overview', 'hero.language': 'overview',
};
export const WARNING_TABS: Record<WarningKey, LandingTab> = {
  'weeks.overlap': 'syllabus', 'weeks.gap': 'syllabus', 'weeks.mismatch': 'syllabus', 'weeks.incomplete': 'syllabus',
  'batch.startedNoWindow': 'overview', 'batch.numberUnbacked': 'alumni', 'plan.invalid': 'pricing',
  'syllabus.outlineEn': 'syllabus', 'syllabus.outlineAr': 'syllabus', 'projects.titleOnly': 'projects',
  'instructor.fallback': 'people', 'instructor.incomplete': 'people', 'instructor.oneLanguage': 'people',
  'schedule.lengthMismatch': 'overview', 'schedule.countMismatch': 'overview', 'demo.present': 'overview',
  'story.stale': 'alumni', 'brochure.demo': 'brochure', 'published.belowGate': 'overview',
  'instructor.partsPartial': 'people', 'instructor.teachesNothing': 'people', 'instructor.notOnBatch': 'people',
  'instructor.moduleHidden': 'people',
};

export interface ReadinessCheck<K extends string = string> {
  key: K;
  tab: LandingTab;
  ok: boolean;
  detail: string;     // English, e.g. "have 1 of 2"; '' when ok
}
export interface LandingWarning { key: WarningKey; tab: LandingTab; detail: string; }
export interface LandingReadiness {
  ready: boolean;                                   // every required check ok
  required: ReadinessCheck<RequiredCheckKey>[];     // always 19, in REQUIRED_CHECK_KEYS order
  recommended: ReadinessCheck<RecommendedCheckKey>[]; // always 16, in order
  warnings: LandingWarning[];                       // tab = WARNING_TABS[key] (published.belowGate: the failing check's tab)
}

/** English copy for the admin checklist and API messages (ADM is English-only). */
export const CHECK_COPY: Record<RequiredCheckKey | RecommendedCheckKey, { label: string; unlocks: string }> = {
  'core.title': { label: 'Title in English and Arabic', unlocks: 'The page heading (edit in Diploma settings).' },
  'core.description': { label: 'Description in English and Arabic', unlocks: 'The search/share description (edit in Diploma settings).' },
  'core.image': { label: 'Cover image', unlocks: 'The WhatsApp/Facebook share preview (edit in Diploma settings).' },
  'core.price': { label: 'Price above 0 EGP', unlocks: 'The price and the checkout (edit in Diploma settings).' },
  'hero.tagline': { label: 'One-line promise in English and Arabic (10–120 characters)', unlocks: 'The line under the title, instead of a cut description.' },
  'hero.level': { label: 'Level', unlocks: 'The level fact in the hero ("For beginners").' },
  'hero.format': { label: 'Delivery format', unlocks: 'The "how it is taught" fact and the missed-session answer.' },
  'duration': { label: 'Duration in weeks', unlocks: 'The duration fact and the syllabus heading.' },
  'fit.audience': { label: 'At least 2 "a good fit if" cards in both languages', unlocks: 'The "Who it is for" section.' },
  'fit.prerequisites': { label: 'At least 1 prerequisite, or "No prerequisites"', unlocks: 'The "Before you start" block.' },
  'outcomes.items': { label: 'At least 4 outcomes in both languages', unlocks: 'The "You will be able to" section.' },
  'outcomes.careers': { label: 'At least 2 career paths in both languages', unlocks: 'The roles band.' },
  'syllabus.modules': { label: 'At least 4 modules, every module titled in both languages', unlocks: 'The syllabus section and the computed duration.' },
  'syllabus.bodies': { label: 'At least half the modules have details in each language', unlocks: 'Expandable modules on both language pages.' },
  'projects.brief': { label: 'At least 1 project with a brief (or 2+ deliverables) in both languages', unlocks: 'The "Projects" section.' },
  'instructor.complete': { label: 'A lead instructor with photo, job title and bio in both languages', unlocks: 'The instructor section and the byline in the hero.' },
  'certificate.complete': { label: 'Certificate criteria in both languages and an anonymised sample', unlocks: 'The full certificate section.' },
  'batches.schedule': { label: 'Every open batch has a schedule (days, time, session length)', unlocks: 'Days and times on the batch cards (set by ops in Batch Management › Config).' },
  'demo.none': { label: 'No demo items on the page', unlocks: 'Publishing on a production server.' },
  'fit.notFor': { label: '1+ "not the right fit if" line', unlocks: 'An honest filter that cuts refunds.' },
  'fit.tech': { label: '1+ technical requirement (laptop, RAM, OS)', unlocks: '"What you will need" under the prerequisites.' },
  'outcomes.six': { label: '6+ outcomes', unlocks: 'A fuller outcomes list.' },
  'outcomes.careersThree': { label: '3+ career paths', unlocks: 'A fuller roles band.' },
  'syllabus.sessionLength': { label: 'Live hours can be computed (session length + sessions per module, or hours per module)', unlocks: 'Hours in the syllabus totals and on each module.' },
  'brochure.ar': { label: 'Arabic brochure PDF', unlocks: 'The download button on the Arabic page.' },
  'brochure.en': { label: 'English brochure PDF', unlocks: 'The download button on the English page.' },
  'pricing.installmentPlan': { label: 'A contracted installment plan', unlocks: '"or from EGP X/month" next to the price.' },
  'alumni.stories': { label: '3+ students with consent (a quote, or a graduate card without one)', unlocks: 'The "People who joined this diploma" cards.' },
  'alumni.projects': { label: '2+ student projects with consent and a screenshot', unlocks: '"What they built".' },
  'alumni.trackRecord': { label: 'Track record (30+ students or 2+ finished batches)', unlocks: 'The track-record line in the proof strip.' },
  'faq.three': { label: '3+ diploma-specific FAQs', unlocks: 'Your answers before the site-wide ones.' },
  'people.support': { label: '2+ support lines', unlocks: 'The "who helps you" rows in "How it runs".' },
  'hero.selfStudy': { label: 'Weekly self-study hours', unlocks: 'The weekly effort line.' },
  'seo.description': { label: 'SEO description in both languages', unlocks: 'A hand-written Google/share description.' },
  'hero.language': { label: 'Language of instruction (confirmed)', unlocks: 'The language fact in the hero and the "What language is it taught in?" answer.' },
};

// ---- admin editor payload (GET/PATCH /v1/diplomas/:id/landing) ------------------------
export interface LandingEditorBatch {
  _id: string;
  name: string;
  number: number | null;
  displayNumber: number | null;
  startDate: string | null;
  endDate: string | null;
  enrolUntil: string | null;
  closesAt: string | null;
  active: boolean;
  state: BatchState;           // includes hidden/finished/closed
  bookable: boolean;
  capacity: number | null;
  enrolled: number;
  schedule: PublicBatchSchedule | null;   // null when no BatchConfig with weekdays
  instructorId: string | null;
  coInstructorIds: string[];   // the batch's co-instructors (Instructor ids), in stored order
  mentorsCount: number;
}
export interface LandingEditorCore {
  _id: string;
  title: string; title_ar: string;
  description: string; description_ar: string;
  slug: string;
  image: string;
  price: number;
  durationWeeks: number;
  active: boolean;
  tracks: { _id: string; title: string; title_ar: string }[];
  batches: LandingEditorBatch[];        // all batches, by startDate asc
  defaultBatchId: string | null;
  fallbackInstructorId: string | null;  // used when landing.instructorIds is empty (active only)
  enrolledAllTime: number;              // = computed.trackRecord.platformStudents
}
export type InstructorMissingField = 'photo' | 'name' | 'name_ar' | 'jobTitle' | 'jobTitle_ar' | 'about' | 'about_ar' | 'inactive';
export interface InstructorOption {
  _id: string;
  name: string; name_ar: string;
  imageUrl: string;
  jobTitle: string; jobTitle_ar: string;
  active: boolean;
  complete: boolean;                   // false when inactive
  missing: InstructorMissingField[];
}
export interface LandingEditorPayload {
  landing: DiplomaLanding;
  core: LandingEditorCore;
  computed: LandingComputed;
  readiness: LandingReadiness;
  sectionsShown: SectionVisibility;        // what the public page renders now, per language (§B.9)
  kayfaStories: { ar: number; en: number };  // Kayfa-wide success stories with text (input of the #stories fallback)
  instructorOptions: InstructorOption[];   // all active instructors + any selected/fallback inactive one
  leads: { waitlist: number; brochure: number; waitlistLast60Days: number };
  env: { demoAllowed: boolean };
  revalidated?: boolean;                   // PATCH only: the site cache was refreshed
}
export interface LandingSummary {           // GET /v1/diplomas/landing-summaries[?leadsSince=ISO]
  _id: string;
  title: string; title_ar: string;
  slug: string;
  active: boolean;
  status: LandingStatus;
  requiredMet: number; requiredTotal: number;
  recommendedMet: number; recommendedTotal: number;
  missingRequired: RequiredCheckKey[];     // "Not campaign-ready: missing …"
  leadsSince: number;                      // leads created after ?leadsSince (0 when absent)
  updatedAt: string | null;
  publishedAt: string | null;
}

// ---- leads ---------------------------------------------------------------------------
export interface DiplomaLeadInput {
  kind: LeadKind;
  name: string;                 // 2..80 for 'waitlist'; '' allowed for 'brochure'
  phone: string;                // E.164, e.g. '+201012345678'
  email: string;                // optional ('' allowed)
  locale: 'ar' | 'en';
  batchId: string | null;
  consent: boolean;             // must be true
  marketingConsent: boolean;
  utm: { source: string; medium: string; campaign: string; content: string; term: string };
  pageUrl: string;
  website: string;              // honeypot: '' for people; filled → 201 and nothing stored (never a 400)
}
export interface DiplomaLead extends Omit<DiplomaLeadInput, 'website' | 'consent'> {
  _id: string;
  diploma: string;
  consent: true;
  createdAt: string;
}

// ---- helpers ---------------------------------------------------------------------------
export function emptyCurriculum(): Curriculum {
  return { phases: [], modules: [], sessionMinutes: null, updatedAt: null, editorNotes: '' };
}
export function emptyLanding(): DiplomaLanding {
  return {
    status: 'draft', publishedAt: null, updatedAt: null, updatedBy: null, editorNotes: '',
    tagline: '', tagline_ar: '', level: '', format: '', instructionLanguage: '',
    weeklySelfStudyHours: null,
    audience: [], notFor: [], prerequisites: [], noPrerequisites: false, techRequirements: [],
    outcomes: [], careers: [],
    curriculum: emptyCurriculum(), tools: [],
    instructorIds: [], support: [],
    pastCohorts: [], stories: [], studentProjects: [],
    certificate: { statement: '', statement_ar: '', criteria: [], sampleImage: '', sampleAnonymised: false },
    installmentPlans: [], included: [], refundSummary: '', refundSummary_ar: '',
    faqs: [], hiddenSiteFaqKeys: [],
    brochure: { en: null, ar: null },
    seo: { title: '', title_ar: '', description: '', description_ar: '', ogImage: '' },
  };
}
/** Latin digits in a batch name: "Data Science – Batch 5" → 5. */
export function parseBatchNumber(name: string): number | null {
  const m = /(?:batch|دفعة|الدفعة)\s*(\d{1,3})/i.exec(name || '');
  return m ? Number(m[1]) : null;
}
